/**
 * The archive (D-25). A soft-deleted blueprint is invisible everywhere else, to
 * administrators too, so this is the only place it can be found, restored or
 * destroyed for good.
 */
import { useState } from 'react';
import { useTerms } from '../../lib/terms';
import { useDocumentTitle } from '../../lib/documentTitle';
import { errorText, useRestoreBlueprint, useArchive, type DeletedBlueprint } from '../../lib/admin';
import { HardDeleteDialog, type DeletionTarget } from './BlueprintDeletion';
import { FormError, formatWhen } from './ui';

const toTarget = (b: DeletedBlueprint): DeletionTarget => ({
  id: b.id,
  name: b.name,
  slug: b.slug,
  clientName: b.client.name,
});

export function ArchiveScreen() {
  const terms = useTerms();
  const { data, isPending, isError, error } = useArchive();
  const restore = useRestoreBlueprint();
  const [purge, setPurge] = useState<DeletionTarget | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  useDocumentTitle('Archive', terms.plural('blueprint'));

  const rows = [...(data ?? [])].sort((a, b) => (b.deletedAt ?? '').localeCompare(a.deletedAt ?? ''));

  return (
    <div className="sheet adm-sheet">
      <div className="masthead">
        <div>
          <h1>Archive</h1>
          <p className="meta">
            Deleted {terms.plural('blueprint')}. Restoring one brings back its versions, content and
            access grants exactly as they were. Deleting permanently cannot be undone.
          </p>
        </div>
      </div>

      {notice ? (
        <p className="status adm-notice" role="status">
          {notice}
        </p>
      ) : null}
      {restore.isError ? <FormError>{errorText(restore.error)}</FormError> : null}

      {isPending ? (
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      ) : isError ? (
        <div className="empty" role="alert">
          Could not load the archive.
          <br />
          {errorText(error)}
        </div>
      ) : rows.length === 0 ? (
        <div className="empty">The archive is empty.</div>
      ) : (
        <div className="adm-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Client</th>
                <th scope="col">Slug</th>
                <th scope="col">Deleted</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id}>
                  <th scope="row" className="adm-name">
                    {b.name}
                  </th>
                  <td>{b.client.name}</td>
                  <td>
                    <code className="adm-code">{b.slug}</code>
                  </td>
                  <td>{formatWhen(b.deletedAt)}</td>
                  <td className="adm-actions">
                    <button
                      type="button"
                      className="btn"
                      disabled={restore.isPending}
                      onClick={() => {
                        setNotice(null);
                        setRestoringId(b.id);
                        restore.mutate(b.id, {
                          onSuccess: () =>
                            setNotice(
                              `‘${b.name}’ (${b.client.name}) was restored, with its versions, content and access grants.`,
                            ),
                          onSettled: () => setRestoringId(null),
                        });
                      }}
                    >
                      {restoringId === b.id ? 'Restoring…' : `Restore ‘${b.name}’`}
                    </button>
                    <button type="button" className="btn danger" onClick={() => setPurge(toTarget(b))}>
                      Delete permanently
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <HardDeleteDialog
        target={purge}
        onClose={() => setPurge(null)}
        onDone={(t) => {
          setPurge(null);
          setNotice(`‘${t.name}’ (${t.clientName}) was permanently deleted.`);
        }}
      />
    </div>
  );
}
